import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Voice dictation button. Records mic audio (optionally a chosen input device) and returns transcript.
// `client` lets callers pass a device-scoped axios instance; defaults to the command `api`.
export function VoiceRecorder({ onTranscript, disabled, client = api, size = "sm" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const deviceId = localStorage.getItem("voiceInputDeviceId");
      const audio = deviceId ? { deviceId: { exact: deviceId } } : true;
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio });
      } catch (_) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = handleStop;
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Geen toegang tot microfoon");
    }
  };

  const stop = () => {
    recRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size === 0) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", blob, "opname.webm");
    try {
      const { data } = await client.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.text) { onTranscript(data.text); toast.success("Spraak omgezet naar tekst"); }
      else toast.info("Geen spraak herkend");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const big = size === "lg";
  return (
    <button
      type="button"
      data-testid="voice-record-btn"
      disabled={disabled || busy}
      onClick={recording ? stop : start}
      className={`inline-flex items-center gap-1.5 rounded-xl font-semibold transition-colors disabled:opacity-60 ${
        big ? "h-12 px-4 text-sm" : "px-2.5 py-1.5 text-xs"
      } ${recording ? "bg-red-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
    >
      {busy ? <Loader2 className={big ? "h-5 w-5 animate-spin" : "h-3.5 w-3.5 animate-spin"} /> : recording ? <Square className={big ? "h-5 w-5" : "h-3.5 w-3.5"} /> : <Mic className={big ? "h-5 w-5" : "h-3.5 w-3.5"} />}
      {busy ? "Verwerken…" : recording ? "Stop" : "Inspreken"}
    </button>
  );
}
